/**
 * LeaderGrowthFocus — the People Leader's view on a team member's
 * Personalised Growth (HR process owner, 8 Sep 2026: before the Annual I&G Review the
 * leader adds to the plan directly, the growth focus included).
 *
 * The team member owns the Growth Focus above this block. Here their People
 * Leader records their own view: the focus they see for the person, why, and
 * — when it is another role — up to three next possible positions from Role
 * Master, each with the readiness for it. The room reads it from the grid;
 * the team member sees it on their plan. When the review's own action set it,
 * it says so.
 */
import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { Compass, Pencil, Plus, Trash2 } from 'lucide-react'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { idpService } from '@/services/api/idpService'
import type { IndividualDevelopmentPlan, NextRole, ReviewFocus } from '@/services/api/idpService'
import { useAuth } from '@/providers/AuthProvider'
import RoleTitlePicker from '@/components/performance/manager/talent-review/RoleTitlePicker'
import { cn } from '@/lib/utils'

export const REVIEW_FOCUS_OPTIONS: { value: ReviewFocus; title: string; subtitle: string }[] = [
  { value: 'IMPROVE_CURRENT', title: 'Current role', subtitle: 'Master the current role' },
  { value: 'ENHANCE_IMPACT', title: 'Expand impact', subtitle: 'Increase their impact where they are' },
  { value: 'FUTURE_ROLE', title: 'Another role', subtitle: 'Advance to a new position' },
]

export const REVIEW_FOCUS_LABEL: Record<ReviewFocus, string> = {
  IMPROVE_CURRENT: 'Current role · master the current role',
  ENHANCE_IMPACT: 'Expand impact · increase their impact',
  FUTURE_ROLE: 'Another role · advance to a new position',
}

const READINESS: { value: NonNullable<NextRole['readiness']>; label: string }[] = [
  { value: 'ready-now', label: 'Ready now' },
  { value: 'ready-1-year', label: 'Planned · 1 to 3 years' },
  { value: 'ready-2-plus', label: 'Potential · 3+ years' },
]
export const NEXT_ROLE_READINESS_LABEL: Record<string, string> = Object.fromEntries(READINESS.map((r) => [r.value, r.label]))

const MAX_ROLES = 3

interface Props {
  plan: IndividualDevelopmentPlan
  /** The viewer is the person's People Leader and may change this block. */
  canEdit: boolean
  /** Re-read the plan after a save. */
  onSaved: () => Promise<void> | void
  className?: string
}

export function LeaderGrowthFocus({ plan, canEdit, onSaved, className }: Props) {
  const { user } = useAuth()
  const [editing, setEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [focus, setFocus] = useState<ReviewFocus | ''>((plan.review_focus as ReviewFocus) || '')
  const [reason, setReason] = useState(plan.review_focus_reason || '')
  const [roles, setRoles] = useState<NextRole[]>(plan.next_roles || [])

  // Follow the plan when it re-loads (the review may have written here).
  useEffect(() => {
    if (editing) return
    setFocus((plan.review_focus as ReviewFocus) || '')
    setReason(plan.review_focus_reason || '')
    setRoles(plan.next_roles || [])
  }, [plan.review_focus, plan.review_focus_reason, plan.next_roles, editing])

  const setBy = plan.review_focus_set_by === 'talent_review'
    ? plan.review_focus_set_by_name || 'the Annual I&G Review'
    : plan.review_focus_set_by_name || null
  const when = plan.review_focus_set_at ? new Date(plan.review_focus_set_at).toLocaleDateString('en-ZA', { dateStyle: 'medium' }) : null

  const save = async () => {
    if (focus === 'FUTURE_ROLE' && roles.filter((r) => r.title.trim()).length === 0) {
      toast.error('Name at least one next possible position, or choose another focus.')
      return
    }
    const missing = roles.find((r) => r.title.trim() && !r.readiness)
    if (missing) {
      toast.error(`${missing.title} still needs a readiness.`)
      return
    }
    setSaving(true)
    try {
      await idpService.updateIDP(plan.id, {
        review_focus: focus || null,
        review_focus_reason: reason.trim() || null,
        review_focus_set_by: user?.employeeId || null,
        review_focus_set_by_name: user?.displayName || user?.email || null,
        next_roles: focus === 'FUTURE_ROLE' ? roles.filter((r) => r.title.trim()).slice(0, MAX_ROLES) : [],
      })
      toast.success('Your view saved.')
      setEditing(false)
      await onSaved()
    } catch (e: any) {
      toast.error(e?.message || 'Could not save your view')
    } finally {
      setSaving(false)
    }
  }

  const updateRole = (i: number, patch: Partial<NextRole>) => setRoles((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)))

  const view = (
    <div className="space-y-2 text-sm">
      {plan.review_focus ? (
        <>
          <p>
            <span className="font-medium">{REVIEW_FOCUS_LABEL[plan.review_focus as ReviewFocus] || plan.review_focus}</span>
          </p>
          {plan.review_focus_reason && <p className="text-gray-700">{plan.review_focus_reason}</p>}
          {plan.review_focus === 'FUTURE_ROLE' && (
            <div>
              <p className="text-xs font-medium text-gray-500">Next possible positions</p>
              {(plan.next_roles || []).length > 0 ? (
                <ul className="mt-1 flex flex-wrap gap-1.5">
                  {(plan.next_roles || []).map((r) => (
                    <li key={r.title}>
                      <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-800">
                        {r.title}
                        {r.readiness ? ` · ${NEXT_ROLE_READINESS_LABEL[r.readiness] || r.readiness}` : ''}
                      </Badge>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-xs text-amber-800">None named yet.</p>
              )}
            </div>
          )}
          {(setBy || when) && (
            <p className="text-xs text-gray-500">
              Set by {setBy || 'their People Leader'}
              {when ? ` · ${when}` : ''}
            </p>
          )}
        </>
      ) : (
        <p className="text-sm text-gray-500">
          {canEdit
            ? 'Not set yet. The Annual I&G Review reads your view from here: the focus you see for them, why, and the next positions if it is another role.'
            : 'Your People Leader has not recorded their view yet.'}
        </p>
      )}
    </div>
  )

  const form = (
    <div className="space-y-4">
      <div>
        <p className="text-sm font-medium">The growth focus you see for them</p>
        <div className="mt-2 grid gap-2 sm:grid-cols-3" role="radiogroup" aria-label="Growth focus">
          {REVIEW_FOCUS_OPTIONS.map((o) => (
            <button
              key={o.value}
              type="button"
              role="radio"
              aria-checked={focus === o.value}
              onClick={() => setFocus(o.value)}
              className={cn(
                'rounded-lg border p-3 text-left transition-colors',
                focus === o.value ? 'border-heritage-blue bg-heritage-blue/5 ring-1 ring-heritage-blue' : 'border-gray-200 hover:border-gray-300',
              )}
              data-demo={`leader-focus-${o.value}`}
            >
              <p className="text-sm font-medium">{o.title}</p>
              <p className="text-xs text-gray-500">{o.subtitle}</p>
            </button>
          ))}
        </div>
      </div>
      <div>
        <label className="text-sm font-medium" htmlFor="leader-focus-reason">
          Why — what you see in them
        </label>
        <Textarea
          id="leader-focus-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={2}
          className="mt-1 text-sm"
          placeholder="What makes this the right focus for them this year"
        />
      </div>
      {focus === 'FUTURE_ROLE' && (
        <div className="space-y-2" data-demo="leader-next-roles">
          <p className="text-sm font-medium">Next possible positions (up to three, from Role Master)</p>
          {roles.map((r, i) => (
            <div key={i} className="flex flex-wrap items-center gap-2">
              <RoleTitlePicker value={r.title} onChange={(title) => updateRole(i, { title })} className="min-w-[16rem] flex-1" ariaLabel={`Next position ${i + 1}`} />
              <Select value={r.readiness || ''} onValueChange={(v) => updateRole(i, { readiness: v as NextRole['readiness'] })}>
                <SelectTrigger className="h-9 w-[11.5rem] text-sm" aria-label={`Readiness for ${r.title || `position ${i + 1}`}`}>
                  <SelectValue placeholder="Readiness" />
                </SelectTrigger>
                <SelectContent>
                  {READINESS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button type="button" variant="ghost" size="sm" className="h-9 px-2 text-gray-500" onClick={() => setRoles((prev) => prev.filter((_, idx) => idx !== i))} aria-label="Remove this position">
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
          {roles.length < MAX_ROLES && (
            <Button type="button" variant="outline" size="sm" onClick={() => setRoles((prev) => [...prev, { title: '', readiness: null }])}>
              <Plus className="mr-1 h-4 w-4" /> Add a position
            </Button>
          )}
        </div>
      )}
      <div className="flex justify-end gap-2">
        <Button
          variant="outline"
          onClick={() => {
            setEditing(false)
            setFocus((plan.review_focus as ReviewFocus) || '')
            setReason(plan.review_focus_reason || '')
            setRoles(plan.next_roles || [])
          }}
          disabled={saving}
        >
          Cancel
        </Button>
        <Button onClick={save} disabled={saving} data-demo="leader-focus-save">
          {saving ? 'Saving…' : 'Save your view'}
        </Button>
      </div>
    </div>
  )

  return (
    <Card className={cn('p-6', className)} data-demo="leader-growth-focus">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-50">
            <Compass className="h-5 w-5 text-emerald-700" />
          </div>
          <div>
            <h3 className="text-base font-semibold">{canEdit ? 'Your view as People Leader' : "Your People Leader's view"}</h3>
            <p className="text-xs text-gray-500">
              {canEdit
                ? 'What the Annual I&G Review reads about their growth: the focus, why, and the next positions if it is another role.'
                : 'The growth focus your People Leader sees for you, and the next positions if it is another role.'}
            </p>
          </div>
        </div>
        {canEdit && !editing && (
          <Button variant="outline" size="sm" onClick={() => setEditing(true)} data-demo="leader-focus-edit">
            <Pencil className="mr-1 h-4 w-4" />
            {plan.review_focus ? 'Edit' : 'Set your view'}
          </Button>
        )}
      </div>
      {editing ? form : view}
    </Card>
  )
}

export default LeaderGrowthFocus
